import { keepPreviousData } from '@tanstack/react-query';
import {
    type ColumnDef,
    type PaginationState,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { BookOpen, MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import type { ListAdminCourses } from '@workspace/client';
import {
    Badge,
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@workspace/shadcn';
import {
    ConfirmDialog,
    SearchInput,
    TableRowSort,
    useAction,
    useData,
} from '@workspace/ui';

import { createdAtColumn } from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import {
    StickyTableBox,
    StickyTableHeader,
} from '../../components/sticky-table-box.tsx';
import { toSar } from './price.ts';

type Course = ListAdminCourses['records'][number];

const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'SAR',
});

export default function CoursesPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [deleteTarget, setDeleteTarget] = useState<Course | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const deleteCourseMutation = useAction('DELETE /admin/courses/{id}', {
    invalidate: ['GET /admin/courses'],
    meta: { toast: true },
    onSuccess: () => {
      setDeleteTarget(null);
      toast.success('Course deleted');
    },
  });

  const columns: ColumnDef<Course>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.title}</div>
      ),
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }) => (
        <div className="tabular-nums">
          {row.original.price === null
            ? 'TBD'
            : priceFormatter.format(toSar(row.original.price))}
        </div>
      ),
    },
    {
      id: 'published',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.published ? (
          <Badge>Live</Badge>
        ) : (
          <Badge variant="outline">Draft</Badge>
        ),
    },
    createdAtColumn(),
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const it = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => navigate(`/courses/${it.id}`)}>
                Edit course
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setDeleteTarget(it);
                }}
              >
                Delete course
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const dataQuery = useData(
    'GET /admin/courses',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      search: searchQuery || undefined,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as 'title' | 'price' | 'createdAt')
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Courses"
        totalCount={`${dataQuery.data?.totalCount || 0} total courses`}
        icon={BookOpen}
      >
        <Button onClick={() => navigate('/courses/new')}>
          <Plus className="mr-2 size-4" />
          Add Course
        </Button>
      </CountablePageHeader>

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        mutation={{
          mutate: () => {
            if (deleteTarget) {
              deleteCourseMutation.mutate({ id: deleteTarget.id });
            }
          },
          isPending: deleteCourseMutation.isPending,
        }}
        title="Delete course"
        description={
          deleteTarget ? (
            <span>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget.title}</strong>? This also deletes every
              enrollment attached to it. This action cannot be undone.
            </span>
          ) : (
            ''
          )
        }
      />
    </div>
  );
}
